import styled from 'styled-components';
import { toast } from 'react-toastify';

import {
    TourStopInput,
    useBuildToursForDateMutation,
    useCreateTourMutation,
    useUpdateTourMutation,
} from 'data/__generated__';

import { stopsOf, type FoundStop, type Tour } from '../helpers';

const placeStops = (tour: Tour, moved: TourStopInput[], at: (kept: TourStopInput[]) => number): TourStopInput[] => {
    const movedIds = new Set(moved.map(({ stopId }) => stopId));
    const kept = stopsOf(tour).filter(({ stopId }) => !movedIds.has(stopId));
    kept.splice(Math.min(at(kept), kept.length), 0, ...moved);
    return kept;
};

/**
 * Every dispatch action in one place: mutation + toast + undo + refetch. All the edits go through the
 * single UPDATE_TOUR snapshot mutation (the API diffs it and derives move/remove/merge); the actions
 * below just compute the right snapshot from the current tour.
 */
export const useTourActions = ({
    birthdayDate,
    refetch,
}: {
    birthdayDate: string;
    refetch: () => Promise<unknown>;
}) => {
    const [buildTours, { loading: building }] = useBuildToursForDateMutation();
    const [createTourMutation] = useCreateTourMutation();
    const [updateTourMutation] = useUpdateTourMutation();

    /** Snapshot update of one tour — `changes` overrides the tour's current {name, deliveryPersonUserId, stops}. */
    const updateTour = (
        tour: Tour,
        changes: Partial<{ name: string; deliveryPersonUserId: string | null; stops: TourStopInput[] }>,
    ) =>
        updateTourMutation({
            variables: {
                _id: tour._id,
                input: {
                    name: tour.name,
                    deliveryPersonUserId: tour.deliveryPersonUserId ?? null,
                    stops: stopsOf(tour),
                    ...changes,
                },
            },
        });

    /** Sends stops back to « à affecter »: removed from the source's snapshot. Undo replays it. */
    const removeStops = (stopIds: string[], message: string, from: Tour) => {
        const removedIds = new Set(stopIds);
        return run(
            'Erreur de retrait',
            () => updateTour(from, { stops: stopsOf(from).filter(({ stopId }) => !removedIds.has(stopId)) }),
            () => toastWithUndo(message, () => updateTour(from, {})),
        );
    };

    /** Success toast with an inline « Annuler » that replays the inverse action — dispatch mistakes are cheap. */
    const toastWithUndo = (message: string, undo: () => Promise<unknown>): void => {
        toast.success(
            ({ closeToast }) => (
                <UndoRow>
                    <span>{message}</span>
                    <UndoButton
                        type="button"
                        onClick={async () => {
                            closeToast();
                            await undo();
                            await refetch();
                            toast.info('Action annulée');
                        }}
                    >
                        Annuler
                    </UndoButton>
                </UndoRow>
            ),
            { autoClose: 8000 },
        );
    };

    /** Runs a mutation, reports the failure to the dispatcher, and always refetches. Returns success. */
    const run = async (action: string, mutate: () => Promise<unknown>, onSuccess?: () => void): Promise<boolean> => {
        try {
            await mutate();
            onSuccess?.();
            return true;
        } catch (error) {
            toast.error(`${action} : ${error instanceof Error ? error.message : String(error)}`);
            return false;
        } finally {
            await refetch();
        }
    };

    return {
        building,

        regenerate: () =>
            run('Erreur de régénération', async () => {
                const result = await buildTours({ variables: { birthdayDate } });
                const summary = result.data?.buildToursForDate;
                if (!summary) return;
                toast.success(
                    `${summary.toursCreated} tournée(s) créée(s), ${summary.toursAppended} complétée(s) — ${summary.deliveriesPlanned} arrêts placés, ${summary.deliveriesKept} déjà composés conservés`,
                );
            }),

        createTour: (stops: TourStopInput[], name: string) =>
            run(
                'Erreur de création',
                () => createTourMutation({ variables: { input: { birthdayDate, stops, name } } }),
                () => toast.success(`Tournée « ${name} » créée`),
            ),

        renameTour: (tour: Tour, name: string) =>
            run(
                'Erreur de renommage',
                () => updateTour(tour, { name }),
                () => toastWithUndo(`Tournée renommée « ${name} »`, () => updateTour(tour, {})),
            ),

        assignDeliveryPerson: (tour: Tour, deliveryPersonUserId: string | null) =>
            run(
                "Erreur d'affectation",
                () => updateTour(tour, { deliveryPersonUserId }),
                () =>
                    toastWithUndo(
                        deliveryPersonUserId ? `Tournée ${tour.name} transférée` : `Tournée ${tour.name} désaffectée`,
                        () => updateTour(tour, {}),
                    ),
            ),

        /**
         * Places one stop (delivery or task) in a tour at the given index in its visit order (end when omitted).
         * Reorder (from === to) and move (the API detaches the stop from its source) are the same operation on the
         * snapshot. Undo replays the source's OLD snapshot: the stop returns to its exact original position.
         */
        moveStop: (stop: TourStopInput, label: string, from: Tour | null, to: Tour, index?: number) =>
            run(
                'Erreur de déplacement',
                () => updateTour(to, { stops: placeStops(to, [stop], (kept) => index ?? kept.length) }),
                () =>
                    toastWithUndo(
                        from?._id === to._id ? `Ordre de ${to.name} mis à jour` : `${label} déplacé vers ${to.name}`,
                        () => updateTour(from ?? to, {}),
                    ),
            ),

        /**
         * Places several stops, in the given order, right after `afterStopId` in a tour (at the end when
         * null) — ONE snapshot, so the map's « déplacer » flow is one mutation and one undo whatever the
         * sources. Undo replays the target's old snapshot; the API re-detaches stops that came from elsewhere.
         */
        moveStops: (
            moved: FoundStop[],
            to: Tour,
            afterStopId: string | null,
            /** The tours the stops came from — needed to put them BACK there on undo. */
            sources: Tour[] = [],
        ) => {
            const stops = placeStops(
                to,
                moved.map(({ stop }) => stop),
                (kept) => (afterStopId ? kept.findIndex(({ stopId }) => stopId === afterStopId) + 1 : kept.length),
            );
            // Undo, in order: the target gives the deliveries back (anything that came from « à affecter »
            // lands there again), then each source reclaims its own. Replaying only the target would scatter
            // deliveries that came from another tour into the loose pool instead of returning them home.
            const undo = async (): Promise<void> => {
                await updateTour(to, {});
                for (const source of sources) await updateTour(source, {});
            };
            return run(
                'Erreur de déplacement',
                () => updateTour(to, { stops }),
                () =>
                    toastWithUndo(
                        moved.length === 1
                            ? `${moved[0].label} placé dans ${to.name}`
                            : `${moved.length} arrêts placés dans ${to.name}`,
                        undo,
                    ),
            );
        },

        removeMovedStops: (moved: FoundStop[], from: Tour) =>
            removeStops(
                moved.map(({ stop }) => stop.stopId),
                moved.length === 1
                    ? `${moved[0].label} retiré de ${from.name}`
                    : `${moved.length} arrêts retirés de ${from.name}`,
                from,
            ),

        removeStop: (stopId: string, label: string, from: Tour) =>
            removeStops([stopId], `${label} retiré de ${from.name}`, from),
    };
};

export type TourActions = ReturnType<typeof useTourActions>;

const UndoRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`;

const UndoButton = styled.button`
    flex-shrink: 0;
    padding: 4px 10px;
    border-radius: 7px;
    border: 1px solid currentColor;
    background: transparent;
    color: inherit;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
`;
